import { toDataURL } from '@progress/kendo-react-excel-export';

/**
 * Exports multiple Kendo ExcelExport workbooks as a single Excel file with multiple sheets.
 *
 * @async
 * @function exportCombinedExcelWorkbooks
 * @param {Object} params - parameters object.
 * @param {Array} params.workbookRefs - Array of refs to Kendo ExcelExport components.
 * @param {Array} params.sheetNames - Array of sheet names for each workbook/sheet.
 * @param {string} params.fileName - name for exported Excel file.
 * @returns {Promise<void>} Resolves when file has been downloaded.
 * @throws {Error} Throws an error if no workbook references are provided or if data URL generation fails.
 */
export const exportCombinedExcelWorkbooks = async ({
  workbookRefs,
  sheetNames,
  fileName,
}) => {
  const noWorkbookRefsError = 'No workbook references provided.';
  const dataURLError = 'Failed to generate data URL for the combined workbook.';
  const processFailureError = 'Failed to export combined Excel workbooks. Please reach out to support if the issue persists.';
  try {
    // checks if workbookRefs is provided and has at least one ref, sets default fileName if not provided
    if (!workbookRefs || workbookRefs.length === 0) throw new Error(noWorkbookRefsError);
    if (!fileName) {
      fileName = 'Combined_Excel_Workbook.xlsx';
    }

    // retrieves workbook options from each ref and combines into one single workbook
    const workbooks = workbookRefs.map(ref => ref.current.workbookOptions());
    const sheets = workbooks.map((wb, i) => ({
      ...wb.sheets[0],
      name: sheetNames[i] || `Sheet${i + 1}`
    }));
    const combinedWorkbook = {
      ...workbooks[0],
      sheets
    };

    // generates data URL for combined workbook
    const dataURL = await toDataURL(combinedWorkbook);
    if (!dataURL) {
      throw new Error(dataURLError);
    }

    // creates anchor link element, triggers download, then removes it
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (err) {
    // log technical error details for debugging
    console.error('Error exporting combined Excel workbooks:', err);
    // throw user-friendly error message for alert
    throw new Error(processFailureError);
  }
}
